console.log('[BOOT] main.tsx starting');

import React from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { lazy } from 'react';
import { Capacitor } from '@capacitor/core';
import App from './App';
import './index.css';
import '@/styles/overrides.css';
import { LazyRouteWrapper } from '@/components/LazyRouteWrapper';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import ProtectedAdminRoute from '@/components/auth/ProtectedAdminRoute';
import { installNaNTraps, BootProofIndicator } from '@/utils/devTools';
import { configureStatusBar } from '@/utils/statusBar';

// Lazy load pages
const Home = lazy(() => import('@/pages/Index'));
const Auth = lazy(() => import('@/pages/auth/AuthPage'));
const ProfileContainer = lazy(() => import('@/pages/Profile/ProfileContainer'));
const Prayers = lazy(() => import('@/pages/prayers/PrayersPage'));
const SharePrayer = lazy(() => import('@/pages/SharePrayerPage'));
const Bible = lazy(() => import('@/pages/Bible'));
const Community = lazy(() => import('@/pages/Community'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'));
const DiscordCallback = lazy(() => import('@/pages/auth/DiscordCallback'));
const Help = lazy(() => import('@/pages/Help'));
const Subscription = lazy(() => import('@/pages/Subscription'));
const SubscriptionSuccess = lazy(() => import('@/pages/SubscriptionSuccess'));
const SubscriptionCancel = lazy(() => import('@/pages/SubscriptionCancel'));
const StreamsOfWorship = lazy(() => import('@/pages/StreamsOfWorship'));
const ESVCopyright = lazy(() => import('@/pages/ESVCopyright'));
const CommunityGuidelines = lazy(() => import('@/pages/CommunityGuidelines'));
const Legal = lazy(() => import('@/pages/Legal'));
const PrivacyPolicy = lazy(() => import('@/pages/privacy'));
const AdminLoginPage = lazy(() => import('@/pages/admin/AdminLoginPage'));
const AdminLoginForm = lazy(() => import('@/pages/admin/AdminLoginForm'));
const Admin = lazy(() => import('@/pages/Admin'));
const VerseOfDayAdmin = lazy(() => import('@/pages/admin/VerseOfDayAdmin'));
const Marketing = lazy(() => import('@/pages/Marketing'));
const PkceHandler = lazy(() => import('@/components/PkceHandler'));
const About = lazy(() => import('@/pages/About'));

// Platform-specific setup
if (Capacitor.getPlatform() === 'ios') {
  document.body.classList.add('ios');
  configureStatusBar();
}

// Development safety traps
installNaNTraps();

// Router configuration
const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        index: true,
        element: (
          <LazyRouteWrapper routeName="Home">
            <Home />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/auth',
        element: (
          <LazyRouteWrapper routeName="Auth">
            <Auth />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/auth/confirm',
        element: <div style={{ display: 'none' }} />,
      },
      {
        path: '/auth/callback',
        element: (
          <LazyRouteWrapper routeName="PkceHandler">
            <PkceHandler />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/auth/forgot-password',
        element: (
          <LazyRouteWrapper routeName="ForgotPassword">
            <ForgotPassword />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/auth/reset-password',
        element: (
          <LazyRouteWrapper routeName="ResetPassword">
            <ResetPassword />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/auth/discord/callback',
        element: (
          <LazyRouteWrapper routeName="DiscordCallback">
            <DiscordCallback />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/profile',
        element: (
          <ProtectedRoute>
            <LazyRouteWrapper routeName="Profile">
              <ProfileContainer />
            </LazyRouteWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: '/prayers',
        element: (
          <LazyRouteWrapper routeName="Prayers">
            <Prayers />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/share-prayer',
        element: (
          <LazyRouteWrapper routeName="SharePrayer">
            <SharePrayer />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/bible',
        element: (
          <LazyRouteWrapper routeName="Bible">
            <Bible />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/community',
        element: (
          <LazyRouteWrapper routeName="Community">
            <Community />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/streams-of-worship',
        element: (
          <LazyRouteWrapper routeName="StreamsOfWorship">
            <StreamsOfWorship />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/esv-copyright',
        element: (
          <LazyRouteWrapper routeName="ESVCopyright">
            <ESVCopyright />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/help',
        element: (
          <LazyRouteWrapper routeName="Help">
            <Help />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/subscription',
        element: (
          <LazyRouteWrapper routeName="Subscription">
            <Subscription />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/subscription-success',
        element: (
          <LazyRouteWrapper routeName="SubscriptionSuccess">
            <SubscriptionSuccess />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/subscription-cancel',
        element: (
          <LazyRouteWrapper routeName="SubscriptionCancel">
            <SubscriptionCancel />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/admin/login',
        element: (
          <LazyRouteWrapper routeName="AdminLoginForm">
            <AdminLoginForm />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/admin',
        element: (
          <ProtectedAdminRoute>
            <LazyRouteWrapper routeName="Admin">
              <Admin />
            </LazyRouteWrapper>
          </ProtectedAdminRoute>
        ),
      },
      {
  path: '/admin/verse-of-day',
  element: (
    <ProtectedAdminRoute>
      <LazyRouteWrapper routeName="VerseOfDayAdmin">
        <VerseOfDayAdmin />
      </LazyRouteWrapper>
    </ProtectedAdminRoute>
  ),
},
       { 
        path: '/community-guidelines',
        element: (
          <LazyRouteWrapper routeName="CommunityGuidelines">
            <CommunityGuidelines />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/legal',
        element: (
          <LazyRouteWrapper routeName="Legal">
            <Legal />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/privacy',
        element: (
          <LazyRouteWrapper routeName="Privacy">
            <PrivacyPolicy />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/terms',
        element: <div style={{ display: 'none' }} />,
        loader: () => {
          window.location.href = '/terms.html';
          return null;
        },
      },
      {
        path: '/ugc-policy',
        element: <div style={{ display: 'none' }} />,
        loader: () => {
          window.location.href = '/ugc-policy.html';
          return null;
        },
      },
      {
        path: '/marketing',
        element: (
          <LazyRouteWrapper routeName="Marketing">
            <Marketing />
          </LazyRouteWrapper>
        ),
      },
      {
        path: '/about',
        element: (
          <LazyRouteWrapper routeName="About">
            <About />
          </LazyRouteWrapper>
        ),
      },
    ],
  },
]);

console.log('[Router] Router configuration initialized');

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container not found');
}

const root = createRoot(container);
root.render(
  <React.StrictMode>
    <BootProofIndicator />
    <RouterProvider router={router} />
  </React.StrictMode>
);
